import { useState } from "react";
import * as React from "react";

import SearchIcon from "@uui/SearchMd";
import emailValidator from "email-validator";
import { compact, find, flatten, partition, map, trim } from "lodash/fp";

import {
  AutocompleteRenderInputParams,
  InputAdornment,
  ListItem,
  Typography,
} from "@mui/material";

import useWorkspaceSearch from "@app/common/access/ShareReport/hooks/useWorkspaceSearch";
import { getAccessLevel } from "@app/common/access/ShareReport/utils";
import useDebounce from "@app/common/hooks/useDebounce";
import type {
  ACLResponse,
  EntityAccessLevel,
  ShareEntity,
  WorkspaceEntity,
  WorkspaceEntityKind,
} from "@app/common/types";
import { getInitials } from "@app/common/utils";
import { lightOrDark } from "@app/embedded/theme/utils";

import Avatar from "@rui/Avatar";
import { Row } from "@rui/Stack";
import TagAutocomplete from "@rui/TagAutocomplete";
import TextField from "@rui/TextField";

type SearchResultWithAccess = WorkspaceEntity & {
  accessLevel?: EntityAccessLevel;
};
export type Option = string | SearchResultWithAccess;

interface AddShareEntitiesInputProps {
  accessList?: ACLResponse;
  emailOptionPrefix?: string;
  getOptions?: (inputValue: string, options: Option[]) => Option[];
  onChange: (value: ShareEntity[]) => void;
  selectedEntities: ShareEntity[];
  workspaceSearchParams?: {
    workspaceId?: string;
    includeEntities?: WorkspaceEntityKind[];
  };
}

interface BaseInputProps {
  emailOptionPrefix: AddShareEntitiesInputProps["emailOptionPrefix"];
  endAdornment?: React.ReactNode;
  inputValue: string;
  getOptions: AddShareEntitiesInputProps["getOptions"];
  onInputChange: (value: string) => void;
  onValueChange: AddShareEntitiesInputProps["onChange"];
  options?: Option[];
  placeholder?: string;
  selectedEntities: AddShareEntitiesInputProps["selectedEntities"];
}

interface AddShareEntitiesWithSearchProps {
  accessList: AddShareEntitiesInputProps["accessList"];
  emailOptionPrefix: AddShareEntitiesInputProps["emailOptionPrefix"];
  getOptions: AddShareEntitiesInputProps["getOptions"];
  inputValue: BaseInputProps["inputValue"];
  onInputChange: BaseInputProps["onInputChange"];
  onValueChange: AddShareEntitiesInputProps["onChange"];
  selectedEntities: AddShareEntitiesInputProps["selectedEntities"];
  workspaceSearchParams: AddShareEntitiesInputProps["workspaceSearchParams"];
}

interface ShareOptionProps extends React.HTMLAttributes<HTMLLIElement> {
  emailOptionPrefix?: string;
  option: Option;
}

const getLabel = (option: ShareEntity) =>
  typeof option === "string" ? option : option.name;

const defaultGetOptions = (inputValue: string, options: Option[]) => options;

const ShareOption = ({
  emailOptionPrefix = "",
  option,
  ...props
}: ShareOptionProps) => {
  const label = getLabel(option);
  const isEmail = typeof option === "string";

  return (
    <ListItem {...props}>
      <Row variant="apart" width="100%">
        <Row variant="left" spacing={1}>
          {!isEmail && <Avatar name={label}>{getInitials(label)}</Avatar>}
          <Typography variant="bodyPrimaryReg">
            {isEmail ? emailOptionPrefix : ""}
            {label}
          </Typography>
        </Row>
        {!isEmail && option.accessLevel && (
          <Typography variant="bodySecondaryReg">
            {getAccessLevel(option.accessLevel).label}
          </Typography>
        )}
      </Row>
    </ListItem>
  );
};

const BaseInput = ({
  emailOptionPrefix,
  endAdornment,
  getOptions = defaultGetOptions,
  inputValue,
  onInputChange,
  onValueChange,
  options = [],
  placeholder = "Add people by email",
  selectedEntities,
}: BaseInputProps) => {
  const handleValueChange = (
    e: React.SyntheticEvent,
    newValue: ShareEntity[]
  ) => {
    const sanitizedValues = newValue.map((entity) =>
      typeof entity === "string" ? trim(entity) : entity
    );
    onValueChange(sanitizedValues);
  };

  const handleInputChange = (e: React.SyntheticEvent, value: string) => {
    onInputChange?.(value);
  };

  const allOptions: Option[] = compact([
    ...options,
    emailValidator.validate(inputValue) ? inputValue : null,
  ]);

  const augmentedOptions = getOptions(inputValue, allOptions);

  return (
    <TagAutocomplete
      name="add-people"
      searchOnType
      multiple
      autoSelect
      autoHighlight
      freeSolo
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField
          placeholder={placeholder}
          {...params}
          sx={{
            "& input::placeholder": {
              color: "shades.neutral.400",
              opacity: 1,
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "shades.neutral.150",
            },
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: params.InputProps.endAdornment ?? endAdornment,
          }}
        />
      )}
      getTagSxProps={(v) =>
        typeof v === "string" && !emailValidator.validate(v)
          ? {
              border: "1px solid",
              borderColor: "shades.negative.500",
              backgroundColor: "shades.negative.100",
            }
          : {}
      }
      getTagLabel={getLabel}
      getOptionLabel={getLabel}
      getOptionDisabled={(option: Option) =>
        typeof option !== "string" && Boolean(option.accessLevel)
      }
      renderOption={(optionProps, option: Option) => (
        <ShareOption
          emailOptionPrefix={emailOptionPrefix}
          option={option}
          {...optionProps}
        />
      )}
      onChange={handleValueChange}
      onInputChange={handleInputChange}
      options={augmentedOptions}
      sx={{
        width: "100%",
        "& .MuiInputBase-root": { padding: "4px 6px" },
        "& .MuiFormControl-root": { minWidth: "100%" },
        svg: {
          color: (theme) =>
            lightOrDark("shades.neutral.400", "shades.neutral.100")(theme),
        },
      }}
      value={selectedEntities}
    />
  );
};

const AddShareEntitiesInputWithSearch = ({
  accessList,
  emailOptionPrefix,
  getOptions,
  inputValue,
  onInputChange,
  onValueChange,
  selectedEntities,
  workspaceSearchParams = {},
}: AddShareEntitiesWithSearchProps) => {
  const { workspaceId, ...searchParams } = workspaceSearchParams;

  const searchWorkspace = useDebounce(onInputChange, 300);
  const { searchResults } = useWorkspaceSearch(
    inputValue,
    workspaceId,
    searchParams
  );

  const augmentedSearchResults: SearchResultWithAccess[] = map(
    (searchResult) => {
      const accessListEntity = find(
        { entityId: searchResult.id },
        accessList?.entityAccess ?? []
      );

      return accessListEntity
        ? { ...searchResult, accessLevel: accessListEntity.accessLevel }
        : searchResult;
    },
    searchResults
  );

  // bring entities who aren't already in the access list to the top
  const sortedSearchResults = flatten(
    partition(
      (searchResult) => !searchResult.accessLevel,
      augmentedSearchResults
    )
  );

  return (
    <BaseInput
      emailOptionPrefix={emailOptionPrefix}
      endAdornment={
        <InputAdornment position="end" sx={{ pr: 1 }}>
          <SearchIcon sx={{ fontSize: "16px" }} />
        </InputAdornment>
      }
      getOptions={getOptions}
      inputValue={inputValue}
      placeholder="Add people by name or email"
      onInputChange={searchWorkspace}
      onValueChange={onValueChange}
      options={sortedSearchResults}
      selectedEntities={selectedEntities}
    />
  );
};

const AddShareEntitiesInput = ({
  accessList,
  emailOptionPrefix,
  getOptions,
  onChange,
  selectedEntities,
  workspaceSearchParams,
}: AddShareEntitiesInputProps) => {
  const [inputValue, setInputValue] = useState("");

  if (workspaceSearchParams?.workspaceId) {
    return (
      <AddShareEntitiesInputWithSearch
        accessList={accessList}
        emailOptionPrefix={emailOptionPrefix}
        getOptions={getOptions}
        inputValue={inputValue}
        onInputChange={setInputValue}
        onValueChange={onChange}
        selectedEntities={selectedEntities}
        workspaceSearchParams={workspaceSearchParams}
      />
    );
  }

  return (
    <BaseInput
      emailOptionPrefix={emailOptionPrefix}
      getOptions={getOptions}
      inputValue={inputValue}
      onInputChange={setInputValue}
      onValueChange={onChange}
      selectedEntities={selectedEntities}
    />
  );
};

export default AddShareEntitiesInput;
